import { Injectable } from '@angular/core';
import { Config } from '@environments/config';
import { Blobs, Permissions } from '@local/client-contracts';
import { BlobsRpcInvoker } from '@local/common';
import { WebCrypto, isEmbed, lookup } from '@local/common-web';
import { getExtensionByFileName, getIconByExtension, isHttpOrHttps } from '@local/ts-infra';
import { ServicesRpcService } from '@shared/services';
import { DownloadsService } from '@shared/services/downloads.service';
import { generateFullPrefixedURL } from '@shared/utils';
import { isString } from 'lodash';
import { BaseFile } from './blobs-storage.service';

export interface ListBlobMetadataEntry {
  id: string;
  lastUpdateTime: number;
}

type CacheEntry = { blob: Blobs.BlobMetadata; lastUpdateTime: number };

@Injectable({
  providedIn: 'root',
})
export class BlobsService {
  private readonly EXTRACT_CREATE_MIME_TYPE = ['text/plain'];
  private readonly EXTRACT_UPLOAD_MIME_TYPE = [...this.EXTRACT_CREATE_MIME_TYPE, 'application/json'];

  private service: Blobs.Service;
  private baseUrl: string;
  private cdnUrl: string;
  private webCrypto: WebCrypto;
  private cache: { [id: string]: CacheEntry } = {};
  private isEmbed: boolean = isEmbed();

  constructor(
    services: ServicesRpcService,
    private downloadService: DownloadsService
  ) {
    this.webCrypto = new WebCrypto();
    this.service = services.invokeWith(BlobsRpcInvoker, 'blobs');
    this.baseUrl = Config.blobs.baseUrl;
    this.cdnUrl = Config.blobs.cdnUrl;
  }

  private getFixedMineType(mimeType: string, fileName: string, extractMimeTypes = []): string {
    if (mimeType && !extractMimeTypes.includes(mimeType)) {
      return mimeType;
    }
    const extractTypeFromName = lookup(fileName);
    return isString(extractTypeFromName) ? extractTypeFromName : '';
  }

  create(name: string, type: string, shareOptions: Permissions.ShareOptions, sizeInBytes?: number) {
    const fixedType = this.getFixedMineType(type, name, this.EXTRACT_CREATE_MIME_TYPE);
    return this.service.create(name, fixedType, shareOptions, sizeInBytes);
  }

  update(id: string, name?: string, type?: string, shareOptions?: Permissions.ShareOptions, sizeInBytes?: number) {
    return this.service.update(id, name, type, shareOptions, sizeInBytes);
  }

  delete(request: Blobs.DeleteBlobRequest) {
    return this.service.delete(request);
  }

  duplicate(request: Blobs.ListDuplicateBlobRequest): Promise<Blobs.ListDuplicateBlobResponse> {
    return this.service.duplicate(request);
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('FileReader result is not an ArrayBuffer'));
        }
      };
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  async upload(file: File, id: string, queryParams: string = ''): Promise<Response> {
    //todo: workaround to card file upload on new, try file.arrayBuffer() after new card creation flow.
    const arrayBuffer = await this.readFileAsArrayBuffer(file);
    const [hash, uploadUrl] = await Promise.all([this.webCrypto.computeSHA256Hex(arrayBuffer), this.getBlobFullUrl(id)]);

    const fixedType = this.getFixedMineType(file.type, file.name, this.EXTRACT_UPLOAD_MIME_TYPE);

    const req = new Request(`${uploadUrl}${queryParams}`, {
      method: 'POST',
      headers: { 'If-Match': hash, 'Content-Type': fixedType },
      body: arrayBuffer,
    });

    return fetch(req);
  }

  getThumbnailUrl(id: string, width = 210, format = 'webp'): string {
    return `${this.isEmbed ? this.cdnUrl : this.baseUrl}/blobs/${id}/thumbnail?width=${width}&format=${format}`;
  }

  getThumbnailFullUrl(id: string, width = 210, format = 'webp'): Promise<string> {
    const url = this.getThumbnailUrl(id, width, format);
    return this.downloadService.addTicketToUrl('blobs', url);
  }

  getCdnUrl(separateUrl: string): Promise<string> {
    const type = isHttpOrHttps(separateUrl) ? 'url' : 'path';
    const url: string = generateFullPrefixedURL(separateUrl, type, true);
    return this.downloadService.addTicketToUrl('blobs', url);
  }

  getBlobFullUrl(id: string): Promise<string> {
    const url = `${this.baseUrl}/blobs/${id}/content`;
    return this.downloadService.addTicketToUrl('blobs', url);
  }

  getDownloadUrl(fileId: string): string {
    return `${this.isEmbed ? this.cdnUrl + '/api' : ''}/blobs/${fileId}/content`;
  }

  async getBlobMetadata(id: string, lastUpdateTime: number): Promise<Blobs.BlobMetadata> {
    let entry = this.getFromCache(id, lastUpdateTime);
    if (!entry) {
      const res = await this.service.getBlobMetadata(id);
      this.cache[id] = entry = { blob: res, lastUpdateTime };
    }
    return entry?.blob;
  }

  async getBlobMetadataByIds(req: ListBlobMetadataEntry[]): Promise<Blobs.ListBlobMetadata> {
    const notCached: { id: string; lastUpdateTime: number; index: number }[] = [];
    const entries: Blobs.BlobMetadata[] = [];
    for (let index = 0; index < req.length; index++) {
      const { id, lastUpdateTime } = req[index];
      const entry = this.getFromCache(id, lastUpdateTime);
      if (!entry) {
        notCached.push({ id, index, lastUpdateTime });
        continue;
      }
      entries[index] = this.cache[id].blob;
    }
    const ids = notCached.map((a) => a.id);
    const res = ids.length ? await this.service.getBlobMetadataByIds(ids) : { entries: [] };
    for (const { id, lastUpdateTime, index } of notCached) {
      const blob = res.entries.find((i) => i.id === id);
      entries[index] = blob;
      if (!blob) {
        continue;
      }
      this.cache[id] = { blob, lastUpdateTime };
    }
    return { entries };
  }

  private getFromCache(id: string, lastUpdateTime: number): CacheEntry {
    const entry = this.cache[id];
    const cacheTime = entry?.lastUpdateTime;
    if (cacheTime && cacheTime >= lastUpdateTime) {
      return entry;
    }
  }

  isBlobApi(url: string) {
    return url.startsWith(`${this.baseUrl}/blobs/`);
  }

  async getFilesByBlobIds(blobIds: string[]): Promise<BaseFile[]> {
    const req = blobIds.map((id) => ({ id, lastUpdateTime: null }));
    const metas = await this.getBlobMetadataByIds(req);
    return metas.entries
      .filter((m) => !!m)
      .map((meta) => {
        return {
          blobId: meta.id,
          mimeType: meta.mimeType,
          name: meta.name,
          icon: getIconByExtension(getExtensionByFileName(meta.name)),
          size: meta.sizeInBytes,
        };
      });
  }
}
